import React, { useEffect, useState } from "react";
import { Button, Flex, Text } from "@chakra-ui/react";
import PathPickBasketTemplateImagePreviewModal from "@/features/pathpick/features/basket/components/buyer-info/PathPickBasketTemplateImagePreviewModal";
import { PathPickSmartStoreImage } from "@/features/pathpick/features/basket/types";
import CustomIcon from "../../../../../../../public/icons-v1_1";

interface Props {
  title: string;
  file: File | null;
  noImageText: string;
  // 서버에 이미 저장된 이미지 파일명 — 새 파일을 선택하기 전까지 표시
  existingFileName?: string;
  // 서버에 이미 저장된 이미지 주소 — 있으면 내려받기 버튼을 보여준다
  existingFileUrl?: string;
  downloadText?: string;
  // 서버에 이미 저장된 이미지 — 새 파일을 선택하기 전까지 표시
  existingImage?: PathPickSmartStoreImage | null;
  // 제목 옆에 붉은 * 필수 표시를 노출할지
  isRequired?: boolean;
  onChangeFile: (file: File | null) => void;
}

// 상세페이지 상/하단 기본 양식 이미지 파일 선택 카드 — 파일명을 누르면 올린 이미지를 미리본다
function PathPickBasketTemplateImageField({
  title,
  file,
  noImageText,
  existingImage,
  existingFileName,
  existingFileUrl,
  downloadText = "다운로드",
  isRequired = false,
  onChangeFile,
}: Props) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [fileObjectUrl, setFileObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setFileObjectUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setFileObjectUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  // 새로 고른 파일이 서버 저장본보다 우선한다
  const previewFileName = file?.name ?? existingImage?.name;
  const previewSrc = file ? fileObjectUrl : existingImage?.url;

  const [isDownloading, setIsDownloading] = useState(false);

  // 저장된 이미지를 원래 파일명으로 내려받는다. S3 주소는 다른 도메인이라 <a download> 가
  // 무시되므로 받아서 저장하고, 받지 못하면(CORS 등) 새 탭으로 연다.
  const handleDownload = async () => {
    if (!existingFileUrl) {
      return;
    }
    setIsDownloading(true);
    try {
      // 미리보기(<img>)가 같은 주소를 CORS 헤더 없이 받아 HTTP 캐시에 남겨 두면,
      // fetch 가 그 캐시를 재사용해 CORS 로 실패한다. 캐시를 거치지 않고 매번 받는다.
      const response = await fetch(existingFileUrl, { cache: "no-store" });
      if (!response.ok) {
        throw new Error(`download failed: ${response.status}`);
      }
      const objectUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = existingFileName || "image";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10 * 1000);
    } catch {
      window.open(existingFileUrl, "_blank", "noopener,noreferrer");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Flex
      flexDirection={"column"}
      gap={"8px"}
      padding={"12px"}
      border={"0.5px solid #D4D4D1"}
      borderRadius={"8px"}
      background={"#FFFFFF"}
    >
      <Flex gap={"6px"} alignItems={"center"}>
        <CustomIcon
          src={"document-text"}
          width={16}
          height={16}
          color={"#1272EF"}
        />
        <Flex gap={"2px"} alignItems={"center"}>
          <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
            {title}
          </Text>
          {isRequired && (
            <Text textStyle={"body-sm-bold"} color={"#EE7D7A"}>
              {"*"}
            </Text>
          )}
        </Flex>
      </Flex>
      <Flex
        padding={"6px 8px"}
        border={"0.5px solid #D4D4D1"}
        borderRadius={"8px"}
      >
        <input
          type={"file"}
          accept={"image/*"}
          style={{ fontSize: "12px", width: "100%" }}
          onChange={(event) => onChangeFile(event.target.files?.[0] ?? null)}
        />
      </Flex>
      <Flex alignItems={"center"} justifyContent={"space-between"} gap={"8px"}>
      {previewFileName && previewSrc ? (
        <Text
          as={"button"}
          type={"button"}
          textStyle={"caption-medium"}
          color={"#1272EF"}
          textDecoration={"underline"}
          textAlign={"left"}
          wordBreak={"break-all"}
          onClick={() => setIsPreviewOpen(true)}
        >
          {previewFileName}
        </Text>
      ) : (
        <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
          {noImageText}
        </Text>
      )}
        {/* 새 파일을 고르기 전, 저장된 이미지가 있을 때만 */}
        {!file && existingFileUrl && (
          <Button
            size={"xs"}
            variant={"outline"}
            flexShrink={0}
            isLoading={isDownloading}
            onClick={handleDownload}
          >
            {downloadText}
          </Button>
        )}
      </Flex>
      {previewFileName && previewSrc && (
        <PathPickBasketTemplateImagePreviewModal
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          title={title}
          fileName={previewFileName}
          src={previewSrc}
          // 다운로드 버튼 비활성화로 미사용 — PreviewModal 복구 시 함께 되살린다
          // file={file}
        />
      )}
    </Flex>
  );
}

export default PathPickBasketTemplateImageField;
